import { useFontLoader } from '@/hooks/useFontLoader';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { useSignupForBetaMutation } from '@/generated/graphql';

export default function Home() {
  const isFontLoaded = useFontLoader({ fonts: ['Jomhuria'] });
  const [betaSignupStatus, setBetaSignupStatus] = useState<
    'initial' | 'email' | 'completed'
  >('initial');
  return (
    <>
      <div
        data-name="MAIN-CONTENT"
        className={`relative z-[2] mx-auto flex min-h-screen w-screen flex-col items-center justify-start bg-home-gradient ${isFontLoaded ? `opacity-100` : `opacity-0`} transition-opacity duration-1000`}
      >
        <div className="mt-2">
          <a
            data-name="BRAND_LOGO"
            className="mx-2 flex items-center gap-2"
            href="/"
          >
            <Logo size="lg" />
            <span className="relative top-[0.1rem] block font-heading text-6xl">
              HappiScreen
            </span>
          </a>
        </div>
        <div className="flex w-screen flex-col items-stretch gap-4">
          <div className="flex w-full flex-col items-center justify-center gap-8 pb-40 pt-40">
            <h1 className="text-heading text-center text-[8vw] font-bold leading-none lg:text-[6vw] xl:text-[4vw]">
              Create Interactive
              <br /> Screens Effortlessly
            </h1>
            <p className="text-center text-xl">
              Currently in private beta.
              <br />
              Sign up to get early access.
            </p>
            <div
              data-name="CTA"
              className="flex flex-col items-center justify-center"
            >
              <Button
                variant="brand"
                size="xl"
                onClick={() => setBetaSignupStatus('email')}
                className={`${betaSignupStatus !== 'initial' ? 'hidden' : ''}`}
              >
                Sign Up For Beta
              </Button>
              {betaSignupStatus === 'email' && (
                <BetaSignupForm
                  onSignupSuccess={() => setBetaSignupStatus('completed')}
                />
              )}
              {betaSignupStatus === 'completed' && (
                <p className="p-2 text-center text-lg italic text-muted-foreground">
                  Thank you for signing up!
                </p>
              )}
            </div>
            <p className="text-center text-xs">
              We will not share your email with anyone.
              <br />
              Built in Scotts Valley, California.
            </p>
          </div>
        </div>
        <div id="detail"></div>
      </div>
    </>
  );
}

function BetaSignupForm({ onSignupSuccess }: { onSignupSuccess: () => void }) {
  const [signupForBeta] = useSignupForBetaMutation();
  const [email, setEmail] = useState('');
  const onSubmit = async (email: string) => {
    await signupForBeta({ variables: { email } });
    onSignupSuccess();
  };

  const emailIsValid = useMemo(() => {
    return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  }, [email]);

  return (
    <form
      className="m-4 flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(email);
      }}
    >
      <Input
        autoFocus
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        replaceClassName
        className="flex h-9 w-full rounded-md border border-input bg-accent px-3 py-4 text-center text-lg shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      />
      <Button type="submit" disabled={!emailIsValid} variant="brand" size="xl">
        Sign up
      </Button>
    </form>
  );
}
